import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button, Card, CardContent, CardHeader, Spinner } from "@learn-me/ui";
import { bundlesApi } from "@/features/bundles/api/bundlesApi";
import { api } from "@/lib/api";
import { formatCurrency } from "@learn-me/utils";
import {
  ArrowLeft,
  Package,
  Shield,
  BookOpen,
  CheckCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import type { ApiResponse } from "@learn-me/types";
import { normalizeImageUrl } from "@/lib/imageUrl";
import TermsCheckbox from "@/components/legal/TermsCheckbox";
import CertificationDisclaimer from "@/components/legal/CertificationDisclaimer";
import {
  LEGAL_TERMS_VERSION,
  LEGAL_PRIVACY_VERSION,
} from "@/content/legal/versions";

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || "";

export default function BundleCheckoutPage() {
  const { bundleId } = useParams<{ bundleId: string }>();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { data: bundle, isLoading } = useQuery({
    queryKey: ["bundle-checkout", bundleId],
    queryFn: () => bundlesApi.getById(bundleId!),
    enabled: !!bundleId,
  });

  const createOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<
        ApiResponse<{ orderId: string; approvalUrl: string }>
      >("/payments/paypal/create-order", {
        bundleId,
        termsAcceptance: {
          termsVersion: LEGAL_TERMS_VERSION,
          privacyVersion: LEGAL_PRIVACY_VERSION,
          contextPath: "/checkout/bundle",
        },
      });
      return response.data.data!;
    },
  });

  const captureOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await api.post<
        ApiResponse<{ bundleEnrollment: unknown }>
      >("/payments/paypal/capture-order", { orderId });
      return response.data.data!;
    },
    onSuccess: () => {
      toast.success("¡Compra realizada con exito!");
      navigate("/checkout/success");
    },
    onError: () => {
      toast.error("Error al procesar el pago. Intenta de nuevo.");
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Paquete no encontrado</h1>
        <Link to="/">
          <Button>Volver al inicio</Button>
        </Link>
      </div>
    );
  }

  if (!bundle.price) {
    navigate(`/bundles/${bundle.slug}`);
    return null;
  }

  return (
    <div className="py-8">
      <div className="container max-w-4xl">
        <Link
          to={`/bundles/${bundle.slug}`}
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al paquete
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Resumen del Pedido</h2>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="w-24 h-16 bg-muted rounded overflow-hidden flex-shrink-0">
                  {bundle.thumbnailUrl ? (
                    <img
                      src={normalizeImageUrl(bundle.thumbnailUrl) ?? ""}
                      alt={bundle.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-medium">{bundle.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Paquete de {bundle.courses?.length || 0} cursos
                  </p>
                </div>
              </div>

              {/* Cursos incluidos */}
              {bundle.courses && bundle.courses.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3">
                    Cursos incluidos:
                  </h4>
                  <ul className="space-y-2">
                    {bundle.courses.map((course) => (
                      <li
                        key={course.id}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {course.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t pt-4 space-y-2">
                {bundle.hasDiscount && bundle.originalPrice && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Precio original</span>
                    <span className="line-through">
                      {formatCurrency(bundle.originalPrice, bundle.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>
                    {formatCurrency(bundle.price ?? 0, bundle.currency)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>
                    {formatCurrency(bundle.price ?? 0, bundle.currency)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Metodo de Pago</h2>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <CertificationDisclaimer variant="compact" />
              </div>
              <div className="mb-4">
                <TermsCheckbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
              </div>
              {PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider
                  options={{
                    clientId: PAYPAL_CLIENT_ID,
                    currency: bundle.currency,
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect" }}
                    disabled={
                      !acceptedTerms ||
                      createOrderMutation.isPending ||
                      captureOrderMutation.isPending
                    }
                    createOrder={async () => {
                      if (!acceptedTerms) {
                        toast.error(
                          "Debes aceptar los Términos y la Política de Privacidad",
                        );
                        throw new Error("terms_not_accepted");
                      }
                      const result = await createOrderMutation.mutateAsync();
                      return result.orderId;
                    }}
                    onApprove={async (data) => {
                      await captureOrderMutation.mutateAsync(data.orderID);
                    }}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Error con PayPal. Intenta de nuevo.");
                    }}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>PayPal no esta configurado.</p>
                  <p className="text-sm mt-2">
                    Contacta al administrador para habilitar pagos.
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Pago seguro con PayPal</span>
              </div>

              {/* Benefits */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium mb-3">Incluye:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    Acceso inmediato a todos los cursos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Certificados de finalizacion
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Actualizaciones gratuitas
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
